/**
 * Module for outputting log messages
 */

import globalEvents from './GlobalEvents'
import type { ValidateTokenExtendedResponse } from '@/TcxApi/Authentication'
import { EmployeeFeature } from '@/TcxApi/Employee'
import type { AxiosInstance } from 'axios'
import type { Ref } from 'vue'

enum LEVEL {
	DEBUG = 0,
	INFO = 1,
	WARNING = 2,
	ERROR = 3
}

interface LogMessage {
	severity: LEVEL
	message: string
	host?: string
	app?: string
	when: Date
}

class Logger {
	/** Where to send logging to */
	destinations = {
		local: true,
		remote: false
	}

	/** Minimum level to report on, below nothing gets outputtted */
	level: LEVEL = LEVEL.INFO

	/** Buffer of messages to send to remote syslog, if remote logging is enabled */
	remoteBuffer: LogMessage[] = []

	/** Buffer of last 2000 log messages, for sending if a realtime request for it is received */
	historyBuffer: LogMessage[] = []

	/** True while we are sending logs to remote syslog - used to ensure singleton run */
	isHandlingRemote = false

	http?: Ref<AxiosInstance>

	remoteDebouncer: ReturnType<typeof setTimeout> | null = null

	/** Used for sending logs in realtime to realtime clients */
	streamCallback?: (severity: string, message: string) => void

	host = 'CommDeskRemote'

	app = 'CommunicatorDesktop-NA'

	loggedIn = false

	constructor() {
		globalEvents.on('Auth:LoggedIn', (auth: ValidateTokenExtendedResponse) => {
			this.loggedIn = true
			if (auth.employeeFeatures.includes(EmployeeFeature.COMMUNICATOR_DEBUG)) {
				this.destinations.remote = true
				this.level = LEVEL.DEBUG
			} else {
				this.destinations.remote = false
				this.level = LEVEL.INFO
				this.remoteBuffer = []
			}
			if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') { // for development
				this.level = LEVEL.DEBUG
			}
			this.app = `CommunicatorDesktop-${auth.employee}`
			this.handleRemote()
		})

		globalEvents.on('Auth:LoggedOut', () => {
			this.loggedIn = false
			this.destinations.remote = false
			this.level = LEVEL.INFO
			if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') { // for development
				this.level = LEVEL.DEBUG
			}
			this.remoteBuffer = []
			this.app = 'CommunicatorDesktop-NA'
		})

		globalEvents.on('MeUpdated', async (token: string) => {
			try {
				if (this.http) {
					const res = await this.http.value.get(`/auth/validateToken?extend=true&token=${token}`)
					const data = res.data as ValidateTokenExtendedResponse

					if (data.success) {
						if (data.employeeFeatures.includes(EmployeeFeature.COMMUNICATOR_DEBUG)) {
							this.destinations.remote = true
							this.level = LEVEL.DEBUG
						} else {
							this.destinations.remote = false
							this.level = LEVEL.INFO
							this.remoteBuffer = []
						}
					}
				}
			}
			catch (_) { /** Ignore */ }
		})
	}

	/**
	 * Common log handler
	 * @param severity Severity level
	 * @param message Log message
	 */
	private async _log(severity: LEVEL, message: string) {
		if (severity >= this.level) {
			if (this.destinations.local) {
				switch (severity) {
					case LEVEL.INFO:
						console.log(`${new Date().toLocaleTimeString()}: INFO: ${message}`) // eslint-disable-line
						break
					case LEVEL.WARNING:
						console.warn(`${new Date().toLocaleTimeString()}: WARNING: ${message}`) // eslint-disable-line
						break
					case LEVEL.ERROR:
						console.error(`${new Date().toLocaleTimeString()}: ERROR: ${message}`) // eslint-disable-line
						break
					case LEVEL.DEBUG:
						console.debug(`${new Date().toLocaleTimeString()}: DEBUG: ${message}`) // eslint-disable-line
						break
				}
			}

			if (this.destinations.remote) {
				this.remoteBuffer.push({ when: new Date(), severity: severity, message: message })
				this.handleRemote()
			}

			this.historyBuffer.push({ when: new Date(), severity: severity, message: message })
			if (this.historyBuffer.length > 2000) {
				this.historyBuffer.splice(0, this.historyBuffer.length - 2000)
			}
		}

		if (this.streamCallback) {
			this.streamCallback(this.level2String(severity), message)
		}
	}

	/**
	 * Handles transmission of log message to remote server
	 * @private
	 */
	async handleRemote() {
		if (this.remoteDebouncer) { clearTimeout(this.remoteDebouncer) }
		this.remoteDebouncer = setTimeout(async () => {
			this.remoteDebouncer = null

			if (this.loggedIn && this.http && this.destinations.remote && !this.isHandlingRemote && this.remoteBuffer.length > 0) {
				this.isHandlingRemote = true

				try {
					let out = []

					for (const item of this.remoteBuffer) {
						item.severity = this.level2String(item.severity) as unknown as LEVEL
						item.host = this.host
						item.app = this.app
						out.push(item)

						if (out.length === 100) {
							await this.http.value.post('/tools/syslog', out)
							out = []
						}
					}

					if (out.length > 0) {
						await this.http.value.post('/tools/syslog', out)
					}

					this.remoteBuffer = []
					this.isHandlingRemote = false
					if (this.remoteBuffer.length > 0) {
						setTimeout(() => { this.handleRemote() }, 0)
					}
				}
				catch (err) {
					console.error(`Failed to send syslog to remote server: ${(err as Error).message}`) // eslint-disable-line
					this.isHandlingRemote = false
				}
			}
		}, 1000)
	}

	level2String(level: LEVEL): string {
		switch (level) {
			case LEVEL.DEBUG: return 'DEBUG'
			case LEVEL.INFO: return 'INFO'
			case LEVEL.WARNING: return 'WARNING'
			case LEVEL.ERROR: return 'ERROR'
		}
	}

	/**
	 * Log an informational message
	 * @param message Log message
	 */
	info(message: string) { this._log(LEVEL.INFO, message) }

	/**
	 * Log an informational message
	 * @param message Log message
	 */
	log(message: string) { this._log(LEVEL.INFO, message) }

	/**
	 * Log a warning messaage
	 * @param message Log message
	 */
	warning(message: string) { this._log(LEVEL.WARNING, message) }

	/**
	 * Log an error message
	 * @param message Log message
	 */
	error(message: string) { this._log(LEVEL.ERROR, message) }

	/**
	 * Log a debug message
	 * @param message Log message
	 */
	debug(message: string) { this._log(LEVEL.DEBUG, message) }
}

export default new Logger()
